import { useTranslation } from 'react-i18next';
import useAuthStore from '../store/authStore';
import { rewriteText } from '../i18n/centerTerms';

export const useStudentLabel = () => {
  const { t } = useTranslation();
  const centerType = useAuthStore((state) => state.centerType);
  const learningSubType = useAuthStore((state) => state.learningSubType);

  const isClinicCenter =
    centerType === 'therapy' || centerType === 'hair_transplant' || centerType === 'pharma_clinic';
  // Sport centers read their students as "Clients" too, but they are still a
  // learning center — so they only change the LABEL. isTherapyCenter below
  // gates clinic-only features and must stay false for them.
  const usesClientLabel = isClinicCenter || (centerType === 'learning' && learningSubType === 'sport');

  const getLabel = (defaultLabel = 'students') => {
    if (usesClientLabel) {
      return t('common.clients');
    }
    return t(`common.${defaultLabel}`) || t('common.students');
  };

  const getSingular = () => {
    if (usesClientLabel) {
      return t('common.clients').slice(0, -1); // Remove the 's' for singular
    }
    return t('common.students').slice(0, -1);
  };

  return { getLabel, getSingular, isTherapyCenter: isClinicCenter };
};

/**
 * useQuranCenter
 * --------------
 * Convenience hook: returns whether the current center is a Quran (Hifz /
 * memorization) sub-type of a learning center. Quran is a SUB-TYPE under
 * centerType === 'learning' (controlled by learningSubType === 'quran'), NOT a
 * sibling of therapy/hair_transplant — so Quran centers inherit every learning
 * feature and clinic centers are never affected.
 */
export const useQuranCenter = () => {
  const centerType = useAuthStore((state) => state.centerType);
  const learningSubType = useAuthStore((state) => state.learningSubType);
  const isQuran = centerType === 'learning' && learningSubType === 'quran';
  return { isQuran };
};

/**
 * useCaffeCenter
 * --------------
 * Convenience hook: returns whether the current center is a Caffe (tournament)
 * sub-type of a learning center. Caffe is a SUB-TYPE under
 * centerType === 'learning' (controlled by learningSubType === 'caffe'),
 * parallel to the Quran sub-type — so caffe centers inherit every learning
 * feature (students, groups) and gain the Tournaments page, while teaching /
 * quran / clinic centers are never affected.
 */
export const useCaffeCenter = () => {
  const centerType = useAuthStore((state) => state.centerType);
  const learningSubType = useAuthStore((state) => state.learningSubType);
  const isCaffe = centerType === 'learning' && learningSubType === 'caffe';
  return { isCaffe };
};

/**
 * useSportCenter
 * --------------
 * Convenience hook: returns whether the current center is a Sport sub-type of
 * a learning center (learningSubType === 'sport'). Sport centers inherit every
 * learning feature; students read as "Clients", groups carry per-session
 * pricing + coach pay, and the dashboard leads with client intake and session
 * scheduling. See docs/sport-center-plan.md.
 */
export const useSportCenter = () => {
  const centerType = useAuthStore((state) => state.centerType);
  const learningSubType = useAuthStore((state) => state.learningSubType);
  const isSport = centerType === 'learning' && learningSubType === 'sport';
  return { isSport };
};

/**
 * useLearningCenter
 * -----------------
 * Every learning center — sport, teaching, quran, other or no sub-type; not
 * caffe (tournaments) and not the clinics. Mirrors backend utils/centerKind.js:
 * the features first built for sport (active / inactive, session counts,
 * merge duplicates, individual lessons + drop-ins) are gated on this.
 * `isPlainLearning` = a learning center that is not sport.
 */
export const isLearningCenterKind = (centerType, learningSubType) =>
  (centerType || 'learning') === 'learning' && learningSubType !== 'caffe';

export const useLearningCenter = () => {
  const centerType = useAuthStore((state) => state.centerType);
  const learningSubType = useAuthStore((state) => state.learningSubType);
  const isLearning = isLearningCenterKind(centerType, learningSubType);
  const isSport = isLearning && learningSubType === 'sport';
  return { isLearning, isPlainLearning: isLearning && !isSport };
};

/**
 * useMultiTeacher
 * ---------------
 * Learning centers other than sport (Quran centers included) can put several
 * teachers on one group / lesson (`teacherIds`, primary first). Sport keeps its
 * own two-coach model, and clinics have no groups.
 */
export const useMultiTeacher = () => {
  const centerType = useAuthStore((state) => state.centerType);
  const learningSubType = useAuthStore((state) => state.learningSubType);
  return { multiTeacher: (centerType || 'learning') === 'learning' && learningSubType !== 'sport' };
};

/**
 * usePharmaCenter
 * ---------------
 * Convenience hook: returns whether the current center is a pharma_clinic
 * (clinical-pharmacist) center. It is a top-level centerType in the clinic
 * family alongside therapy / hair_transplant — recipients read as "Clients" —
 * but its only screens are Clients and Orders (orders flow in from the public
 * marketing site). Used to gate the Orders nav/route and pharma settings.
 */
export const usePharmaCenter = () => {
  const centerType = useAuthStore((state) => state.centerType);
  const isPharma = centerType === 'pharma_clinic';
  return { isPharma };
};

/**
 * Enhanced translation hook that automatically converts "student" references to "client"
 * when centerType is 'therapy'
 * Usage: const t = useSmartTranslation(); then use it like regular t()
 */
export const useSmartTranslation = () => {
  const { t: originalT, i18n } = useTranslation();
  const centerType = useAuthStore((state) => state.centerType);
  const learningSubType = useAuthStore((state) => state.learningSubType);
  const isTherapy =
    centerType === 'therapy' || centerType === 'hair_transplant' || centerType === 'pharma_clinic';
  // Quran (Hifz) sub-type of a learning center: re-label generic education
  // terms (groups -> halaqat, lessons -> recitation sessions) without changing
  // any underlying data model. Mutually exclusive with the therapy mappings.
  const isQuran = centerType === 'learning' && learningSubType === 'quran';
  // Sport sub-type: students are "Clients" (label only, same data model).
  const isSport = centerType === 'learning' && learningSubType === 'sport';

  // key -> { en, ar, he } overrides applied only for Quran centers.
  const quranMappings = {
    'sidebar.groups': { en: 'Halaqat', ar: 'الحلقات', he: 'חלקות' },
    'groups.title': { en: 'Halaqat', ar: 'الحلقات', he: 'חלקות' },
    'groups.group': { en: 'Halaqa', ar: 'حلقة', he: 'חלקה' },
    'sidebar.lessons': { en: 'Recitation Sessions', ar: 'جلسات التسميع', he: 'מפגשי דקלום' },
    'lessons.title': { en: 'Recitation Sessions', ar: 'جلسات التسميع', he: 'מפגשי דקלום' },
    'lessons.addLesson': { en: 'Add Recitation Session', ar: 'إضافة جلسة تسميع', he: 'הוסף מפגש דקלום' },
    'sidebar.teachers': { en: 'Teachers (Muʿallimun)', ar: 'المعلمون', he: 'מורים' },
  };

  // Map of translation key patterns that should be converted for therapy centers
  const studentToClientMappings = {
    'sidebar.students': { en: 'Clients', ar: 'العملاء', he: 'לקוחות' },
    'breadcrumbs.students': { en: 'Clients', ar: 'العملاء', he: 'לקוחות' },
    'students.title': { en: 'Clients', ar: 'العملاء', he: 'לקוחות' },
    'students.addStudent': { en: 'Add Client', ar: 'إضافة عميل', he: 'הוסף לקוח' },
    'students.editStudent': { en: 'Edit Client', ar: 'تعديل عميل', he: 'ערוך לקוח' },
    'students.deleteStudent': { en: 'Delete Client', ar: 'حذف عميل', he: 'מחק לקוח' },
    'students.noStudents': { en: 'No clients found', ar: 'لم يتم العثور على عملاء', he: 'לא נמצאו לקוחות' },
    'students.parentName': { en: 'Parent/Guardian Name', ar: 'اسم الوالد/الولي', he: 'שם הורה/אפוטרופוס' },
    'students.parentEmail': { en: 'Parent/Guardian Email', ar: 'بريد الوالد/الولي الإلكتروني', he: 'אימייל הורה/אפוטרופוס' },
    'students.parentPhone': { en: 'Parent/Guardian Phone', ar: 'هاتف الوالد/الولي', he: 'טלפון הורה/אפוטרופוס' },
    'dashboard.totalStudents': { en: 'Total Clients', ar: 'إجمالي العملاء', he: 'סך הכל לקוחות' },
    'students.noStudentsYet': { en: 'No clients yet', ar: 'لا يوجد عملاء بعد', he: 'אין לקוחות עדיין' },
    'students.getStarted': { en: 'Get started by adding your first client.', ar: 'ابدأ بإضافة أول عميل.', he: 'התחל על ידי הוספת הלקוח הראשון שלך.' },
    'students.deleteConfirm': { en: 'Are you sure you want to delete this client?', ar: 'هل أنت متأكد أنك تريد حذف هذا العميل؟', he: 'האם אתה בטוח שברצונך למחוק לקוח זה?' },
    'students.notFound': { en: 'Client not found', ar: 'العميل غير موجود', he: 'הלקוח לא נמצא' },
    'students.noContactInfo': { en: 'No contact information available', ar: 'لا توجد معلومات اتصال', he: 'אין מידע ליצירת קשר' },
    'students.therapyInfo': { en: 'Therapy Information', ar: 'معلومات العلاج', he: 'פרטי טיפול' },
    'students.referralSource': { en: 'Referral Source', ar: 'مصدر الإحالة', he: 'מקור הפניה' },
    'students.intakeNotes': { en: 'Intake Notes', ar: 'ملاحظات القبول', he: 'הערות קליטה' },
    'students.initialStatus': { en: 'Initial Status', ar: 'الحالة الأولية', he: 'סטטוס התחלתי' },
    'students.bulkDeleteConfirm': { en: 'Delete {{count}} clients?', ar: 'حذف {{count}} عملاء؟', he: 'למחוק {{count}} לקוחות?' },
    'breadcrumbs.studentDetails': { en: 'Client Details', ar: 'تفاصيل العميل', he: 'פרטי לקוח' },
    'messages.studentAdded': { en: 'Client {{name}} added successfully', ar: 'تمت إضافة العميل {{name}} بنجاح', he: 'הלקוח {{name}} נוסף בהצלחה' },
    'messages.studentDeleted': { en: 'Client {{name}} deleted successfully', ar: 'تم حذف العميل {{name}} بنجاح', he: 'הלקוח {{name}} נמחק בהצלחה' },
    'survey.fillTip': { en: 'To fill a survey for a client, go to their profile page.', ar: 'لملء استبيان لعميل، انتقل إلى صفحة ملفه الشخصي.', he: 'למילוי סקר עבור לקוח, עברו לדף הפרופיל שלו.' },
  };

  // Keys that only make sense in a clinic — a sport center keeps the learning
  // wording for these even though it borrows the Students -> Clients map.
  const clinicOnlyKeys = new Set([
    'students.therapyInfo', 'students.referralSource', 'students.intakeNotes',
    'students.initialStatus', 'students.parentName', 'students.parentEmail', 'students.parentPhone',
  ]);

  // Learning-dashboard / quick-action keys that clinics never render, so they
  // live apart from the therapy map.
  const sportMappings = {
    'learningDashboard.kpi.students': { en: 'Clients', ar: 'العملاء', he: 'לקוחות' },
    'learningDashboard.kpi.todayLessons': { en: 'Sessions today', ar: 'جلسات اليوم', he: 'מפגשים היום' },
    'learningDashboard.upcomingLessons': { en: 'Upcoming sessions', ar: 'الجلسات القادمة', he: 'מפגשים קרובים' },
    'learningDashboard.empty': {
      en: 'No clients, groups, or sessions yet. Start with a client intake or by creating a group.',
      ar: 'لا يوجد عملاء أو مجموعات أو جلسات بعد. ابدأ باستقبال عميل أو بإنشاء مجموعة.',
      he: 'אין עדיין לקוחות, קבוצות או מפגשים. התחילו בקליטת לקוח או ביצירת קבוצה.',
    },
    'learningDashboard.insights.lowAttendanceDesc': {
      en: '{{count}} clients below 70%', ar: '{{count}} عملاء أقل من 70%', he: '{{count}} לקוחות מתחת ל-70%',
    },
    'dashboard.addStudent': { en: 'Add Client', ar: 'إضافة عميل', he: 'הוסף לקוח' },
    'dashboard.createLesson': { en: 'Schedule Session', ar: 'جدولة جلسة', he: 'קביעת מפגש' },
    'learningDashboard.title': { en: 'Sport dashboard', ar: 'لوحة التحكم - رياضة', he: 'לוח בקרה - ספורט' },
    'dashboard.activeStudents': { en: 'Active Clients', ar: 'العملاء النشطون', he: 'לקוחות פעילים' },
    'dashboard.searchPlaceholder': {
      en: 'Search clients, coaches, groups...', ar: 'ابحث عن عملاء، مدربين، مجموعات...', he: 'חיפוש לקוחות, מאמנים, קבוצות...',
    },
    'fab.addStudent': { en: 'Add client', ar: 'إضافة عميل', he: 'הוספת לקוח' },
    'students.searchStudent': { en: 'Search Client', ar: 'البحث عن عميل', he: 'חיפוש לקוח' },
    'students.startTypingToSearch': {
      en: 'Start typing to search for clients...', ar: 'ابدأ الكتابة للبحث عن عملاء...', he: 'התחילו להקליד כדי לחפש לקוחות...',
    },
    'students.studentAdded': { en: 'Client added successfully', ar: 'تمت إضافة العميل بنجاح', he: 'הלקוח נוסף בהצלחה' },
    'students.studentUpdated': { en: 'Client updated successfully', ar: 'تم تحديث العميل بنجاح', he: 'הלקוח עודכן בהצלחה' },
    'students.studentDeleted': { en: 'Client deleted successfully', ar: 'تم حذف العميل بنجاح', he: 'הלקוח נמחק בהצלחה' },
    'lessons.title': { en: 'Sessions', ar: 'الجلسات', he: 'מפגשים' },
    'sidebar.lessons': { en: 'Sessions', ar: 'الجلسات', he: 'מפגשים' },
    'lessons.addLesson': { en: 'Add Session', ar: 'إضافة جلسة', he: 'הוסף מפגש' },
    'sidebar.jibaya': { en: 'Billing', ar: 'الفواتير', he: 'חיובים' },
    'sidebar.teachers': { en: 'Coaches', ar: 'المدربون', he: 'מאמנים' },
    // Sessions page
    'lessons.subtitle': { en: 'Manage daily sessions', ar: 'إدارة الجلسات اليومية', he: 'ניהול מפגשים יומי' },
    'lessons.newLesson': { en: 'New session', ar: 'جلسة جديدة', he: 'מפגש חדש' },
    'lessons.editLesson': { en: 'Edit session', ar: 'تعديل الجلسة', he: 'עריכת מפגש' },
    'lessons.allLessons': { en: 'All sessions', ar: 'جميع الجلسات', he: 'כל המפגשים' },
    'lessons.lesson': { en: 'Session', ar: 'جلسة', he: 'מפגש' },
    'lessons.nextLesson': { en: 'Next session', ar: 'الجلسة القادمة', he: 'המפגש הבא' },
    'lessons.regularLesson': { en: 'Regular session', ar: 'جلسة عادية', he: 'מפגש רגיל' },
    'lessons.makeupClass': { en: 'Make-up session', ar: 'جلسة تعويضية', he: 'מפגש השלמה' },
    'lessons.sessionType': { en: 'Session type', ar: 'نوع الجلسة', he: 'סוג מפגש' },
    'lessons.teacher': { en: 'Coach', ar: 'المدرب', he: 'מאמן' },
    'lessons.selectTeacher': { en: 'Select coach', ar: 'اختر المدرب', he: 'בחירת מאמן' },
    'lessons.noStudentsInGroup': { en: 'No clients in this group', ar: 'لا يوجد عملاء في هذه المجموعة', he: 'אין לקוחות בקבוצה זו' },
    'lessons.notesPlaceholder': { en: 'Optional notes about this session...', ar: 'ملاحظات اختيارية حول الجلسة...', he: 'הערות אופציונליות על המפגש...' },
    'lessons.confirmDelete': { en: 'Delete this session?', ar: 'حذف هذه الجلسة؟', he: 'למחוק את המפגש?' },
    'lessons.copyWeekConfirm': {
      en: 'This will copy all sessions from last week to the current week. Continue?',
      ar: 'سيتم نسخ جميع الجلسات من الأسبوع الماضي إلى الأسبوع الحالي. متابعة؟',
      he: 'פעולה זו תעתיק את כל המפגשים משבוע שעבר לשבוע הנוכחי. להמשיך?',
    },
    'lessons.noLessons': { en: 'No sessions scheduled for this day', ar: 'لا توجد جلسات مجدولة لهذا اليوم', he: 'אין מפגשים מתוכננים ליום זה' },
    'emptyStates.noLessons.title': { en: 'No sessions scheduled', ar: 'لا توجد جلسات مجدولة', he: 'אין מפגשים מתוכננים' },
    'emptyStates.noLessons.description': {
      en: 'Schedule sessions for your groups to track attendance and billing.',
      ar: 'جدول جلسات لمجموعاتك لمتابعة الحضور والفواتير.',
      he: 'קבעו מפגשים לקבוצות שלכם כדי לעקוב אחר נוכחות וחיובים.',
    },
    'emptyStates.noStudents.title': { en: 'No clients yet', ar: 'لا يوجد عملاء بعد', he: 'אין לקוחות עדיין' },
    'emptyStates.noStudents.description': {
      en: 'Start with a client intake — the client fills in and signs the form on WhatsApp.',
      ar: 'ابدأ باستقبال عميل — يعبّئ العميل النموذج ويوقّع عليه عبر واتساب.',
      he: 'התחילו בקליטת לקוח — הלקוח ממלא וחותם על הטופס בוואטסאפ.',
    },
    'emptyStates.noTeachers.title': { en: 'No coaches yet', ar: 'لا يوجد مدربون بعد', he: 'אין מאמנים עדיין' },
    'emptyStates.noTeachers.description': {
      en: 'Add your coaches to assign them to groups and track their pay.',
      ar: 'أضف المدربين لتعيينهم للمجموعات ومتابعة أجورهم.',
      he: 'הוסיפו מאמנים כדי לשבץ אותם לקבוצות ולעקוב אחר השכר שלהם.',
    },
    'emptyStates.noGroups.description': {
      en: 'Create a group for each training — set its coach and price per session.',
      ar: 'أنشئ مجموعة لكل تدريب — حدد المدرب وسعر الجلسة.',
      he: 'צרו קבוצה לכל אימון — קבעו מאמן ומחיר למפגש.',
    },
    // Clients page
    'common.students': { en: 'Clients', ar: 'العملاء', he: 'לקוחות' },
    'studentDetail.title': { en: 'Client details', ar: 'تفاصيل العميل', he: 'פרטי לקוח' },
    'students.importMap.exportCsv': { en: 'Export clients (CSV)', ar: 'تصدير العملاء (CSV)', he: 'ייצוא לקוחות (CSV)' },
    'students.importMap.importCsv': { en: 'Import clients (CSV)', ar: 'استيراد العملاء (CSV)', he: 'ייבוא לקוחות (CSV)' },
    'students.importMap.exportEmpty': { en: 'No clients to export', ar: 'لا يوجد عملاء للتصدير', he: 'אין לקוחות לייצוא' },
    'students.importSuccess': { en: 'Clients imported successfully', ar: 'تم استيراد العملاء بنجاح', he: 'הלקוחות יובאו בהצלחה' },
    'errors.addStudentFailed': { en: 'Error adding client', ar: 'خطأ في إضافة العميل', he: 'שגיאה בהוספת לקוח' },
    'errors.deleteStudentFailed': { en: 'Error deleting client', ar: 'خطأ في حذف العميل', he: 'שגיאה במחיקת לקוח' },
    // Groups page
    'groups.subtitle': { en: 'Manage groups, coaches and clients', ar: 'إدارة المجموعات والمدربين والعملاء', he: 'ניהול קבוצות, מאמנים ולקוחות' },
    'groups.teacher': { en: 'Coach', ar: 'المدرب', he: 'מאמן' },
    'groups.selectTeacher': { en: 'Select coach', ar: 'اختر المدرب', he: 'בחירת מאמן' },
    'groups.allTeachers': { en: 'All coaches', ar: 'جميع المدربين', he: 'כל המאמנים' },
    'groups.students': { en: 'Clients', ar: 'العملاء', he: 'לקוחות' },
    'groups.addStudent': { en: 'Add client', ar: 'إضافة عميل', he: 'הוסף לקוח' },
    'groups.manageStudents': { en: 'Manage clients', ar: 'إدارة العملاء', he: 'ניהול לקוחות' },
    'groups.lessons': { en: 'Sessions', ar: 'الجلسات', he: 'מפגשים' },
    'groups.lessonsCount': { en: 'Number of sessions', ar: 'عدد الجلسات', he: 'מספר מפגשים' },
    'groups.lessonDuration': { en: 'Session duration', ar: 'مدة الجلسة', he: 'משך מפגש' },
    'groups.noLessonsRecorded': { en: 'No sessions recorded yet', ar: 'لم يتم تسجيل جلسات بعد', he: 'טרם נרשמו מפגשים' },
    'groups.attendanceDetailsSubtitle': { en: 'Per-session attendance breakdown', ar: 'تفصيل الحضور لكل جلسة', he: 'פירוט נוכחות לכל מפגש' },
    'groupDetails.lesson': { en: 'Session', ar: 'جلسة', he: 'מפגש' },
    'groups.getStartedGroups': {
      en: 'Create a group for each training — set its coach and price per session.',
      ar: 'أنشئ مجموعة لكل تدريب — حدد المدرب وسعر الجلسة.',
      he: 'צרו קבוצה לכל אימון — קבעו מאמן ומחיר למפגש.',
    },
    'teachers.title': { en: 'Coaches', ar: 'المدربون', he: 'מאמנים' },
    'teachers.quickAddTeacher': { en: 'Quick add coach', ar: 'إضافة مدرب سريعة', he: 'הוספת מאמן מהירה' },
    'teachers.registerTeacherTitle': { en: 'Register new coach', ar: 'تسجيل مدرب جديد', he: 'רישום מאמן חדש' },
    'teachers.editTeacherTitle': { en: 'Edit coach', ar: 'تعديل مدرب', he: 'עריכת מאמן' },
    'teachers.deleteTeacherTitle': { en: 'Delete coach', ar: 'حذف المدرب', he: 'מחיקת מאמן' },
    'teachers.noTeachersYet': { en: 'No coaches yet', ar: 'لا يوجد مدربون بعد', he: 'אין מאמנים עדיין' },
    'teachers.noTeachersFound': { en: 'No coaches found', ar: 'لم يتم العثور على مدربين', he: 'לא נמצאו מאמנים' },
    'teachers.getStartedTeacher': { en: 'Get started by adding your first coach.', ar: 'ابدأ بإضافة أول مدرب.', he: 'התחילו בהוספת המאמן הראשון.' },
    'teachers.searchTeachers': { en: 'Search coaches...', ar: 'ابحث عن مدربين...', he: 'חיפוש מאמנים...' },
    'teachers.teacherAdded': { en: 'Coach added successfully', ar: 'تمت إضافة المدرب بنجاح', he: 'המאמן נוסף בהצלחה' },
    'teachers.teacherUpdated': { en: 'Coach updated successfully', ar: 'تم تحديث المدرب بنجاح', he: 'המאמן עודכן בהצלחה' },
    'teachers.teacherDeleted': { en: 'Coach deleted successfully', ar: 'تم حذف المدرب بنجاح', he: 'המאמן נמחק בהצלחה' },
    'teachers.roleTeacher': { en: 'Coach', ar: 'مدرب', he: 'מאמן' },
    'teachers.addTeacher': { en: 'Add coach', ar: 'إضافة مدرب', he: 'הוספת מאמן' },
    'teachers.editTeacher': { en: 'Edit coach', ar: 'تعديل المدرب', he: 'עריכת מאמן' },
    'teachers.deleteTeacher': { en: 'Delete coach', ar: 'حذف المدرب', he: 'מחיקת מאמן' },
    'teachers.deleteConfirm': { en: 'Delete this coach?', ar: 'حذف هذا المدرب؟', he: 'למחוק את המאמן?' },
    'teachers.registerSubtitle': { en: 'Enter the coach details to create an account', ar: 'أدخل بيانات المدرب لإنشاء حساب', he: 'הזינו את פרטי המאמן להקמת חשבון' },
    'teachers.registerWithLogin': { en: 'Add coach (with login)', ar: 'إضافة مدرب (مع تسجيل دخول)', he: 'הוספת מאמן (עם התחברות)' },
    'teachers.roleHint': {
      en: 'Managers manage the whole center; coaches only see their own groups.',
      ar: 'يدير المدير المركز بالكامل؛ ويرى المدرب مجموعاته فقط.',
      he: 'מנהל מנהל את כל המרכז; מאמן רואה רק את הקבוצות שלו.',
    },
    'teachers.editTeacherInfo': { en: 'Edit coach information', ar: 'تعديل معلومات المدرب', he: 'עריכת פרטי מאמן' },
    'teachers.noGroupsAssigned': { en: 'No groups assigned to this coach yet.', ar: 'لا توجد مجموعات مسندة لهذا المدرب بعد.', he: 'אין קבוצות משויכות למאמן זה עדיין.' },
    'teachers.loadFailed': { en: 'Failed to load coach data', ar: 'فشل تحميل بيانات المدرب', he: 'טעינת נתוני המאמן נכשלה' },
    'teachers.bulkDeleteTeachersConfirm': { en: 'Delete {{count}} coaches?', ar: 'حذف {{count}} مدربين؟', he: 'למחוק {{count}} מאמנים?' },
  };

  const interpolate = (mapping, options) => {
    let result = mapping[i18n.language] || mapping.en;
    if (options && typeof result === 'string') {
      Object.keys(options).forEach(k => {
        result = result.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), options[k]);
      });
    }
    return result;
  };

  const t = (key, options = {}) => {
    // Sport sub-type: its own keys first, then the shared Students -> Clients
    // map (minus the clinic-only wording).
    // The maps say "Clients"/"Sessions"; the sport wording rules
    // (i18n/centerTerms.js) turn that into trainees / training sessions.
    if (isSport) {
      if (sportMappings[key]) return rewriteText(interpolate(sportMappings[key], options), 'sport', i18n.language);
      if (studentToClientMappings[key] && !clinicOnlyKeys.has(key)) {
        return rewriteText(interpolate(studentToClientMappings[key], options), 'sport', i18n.language);
      }
    }

    // Quran sub-type terminology overrides (learning centers only).
    if (isQuran && quranMappings[key]) {
      const mapping = quranMappings[key];
      const lang = i18n.language;
      let result = mapping[lang] || mapping.en;
      if (options && typeof result === 'string') {
        Object.keys(options).forEach(k => {
          result = result.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), options[k]);
        });
      }
      return result;
    }

    // If it's a therapy center, check if we have a mapping for this key
    if (isTherapy && studentToClientMappings[key]) {
      const mapping = studentToClientMappings[key];

      // If mapping is an object with language variants, use the current language
      if (typeof mapping === 'object') {
        const lang = i18n.language;
        let result = mapping[lang] || mapping.en;
        // Interpolate any {{key}} placeholders from options
        if (options && typeof result === 'string') {
          Object.keys(options).forEach(k => {
            result = result.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), options[k]);
          });
        }
        return result;
      }

      // If mapping is a translation key string, resolve it
      if (typeof mapping === 'string') {
        return originalT(mapping, options);
      }
    }

    // Fall back to original translation
    return originalT(key, options);
  };

  return t;
};
